/**
 * The prospects pages' glyphs that this app's icon set does not have.
 *
 * Drawn the way `components/icons.tsx` draws: a 24x24 stroke path on the same
 * grid, 1.75 stroke, round caps and joins, decorative by default and sized by
 * the same class, so one sits level with its neighbours. Ported from radar
 * `src/components/icons.tsx` (Review, Target, Trend, PanelClose, PanelOpen)
 * and from the inline SVGs the old pages drew where they needed one (the list
 * toggle, the paperclip, the review stars, the post's counts). Everything else
 * a page needs is already in `components/icons.tsx`: check, close, search,
 * menu, the chevrons, refresh, info, external, edit, people, analytics and the
 * rest. Use those, and add here only what neither file has.
 *
 * Kept here rather than added to `components/icons.tsx`, which is shared and
 * edited from several directions, because these have callers on the prospects
 * pages and the sidebar's Prospecting rows only.
 */
import type { SVGProps } from 'react'
import { cn } from '../../../lib/cn'
import type { IconProps } from '../../../components/icons'

function Icon({ className, ...props }: SVGProps<SVGSVGElement>) {
  return (
    <svg
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.75}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      focusable="false"
      className={cn('h-[1.15em] w-[1.15em] shrink-0', className)}
      {...props}
    />
  )
}

/** Review: two ticks, one behind the other (several things signed off at once). */
export const IconReview = (p: IconProps) => (
  <Icon {...p}>
    <path d="m3.5 12.5 4 4 8.5-9" />
    <path d="m12 16.5 1 1 7.5-8" />
  </Icon>
)

/** Prospects: people likely to buy. The sidebar's Prospecting glyph. */
export const IconTarget = (p: IconProps) => (
  <Icon {...p}>
    <circle cx="12" cy="12" r="8" />
    <circle cx="12" cy="12" r="4.5" />
    <path d="M12 12h.01" />
  </Icon>
)

/** Trends: what people search for and talk about today. */
export const IconTrend = (p: IconProps) => (
  <Icon {...p}>
    <path d="m4 16.5 5-5 3.5 3.5 7.5-7.5" />
    <path d="M15 7.5h5v5" />
  </Icon>
)

/** A side panel, with the way it will move: fold it away. */
export const IconPanelClose = (p: IconProps) => (
  <Icon {...p}>
    <rect x="3.5" y="4.5" width="17" height="15" rx="2" />
    <path d="M9.5 4.5v15M15.5 10l-2 2 2 2" />
  </Icon>
)

/** A side panel, with the way it will move: open it out. */
export const IconPanelOpen = (p: IconProps) => (
  <Icon {...p}>
    <rect x="3.5" y="4.5" width="17" height="15" rx="2" />
    <path d="M9.5 4.5v15M13.5 10l2 2-2 2" />
  </Icon>
)

/** List: the One at a time view's way back to every card (review/Inbox.tsx). */
export const IconList = (p: IconProps) => (
  <Icon {...p}>
    <path d="M8 6h12M8 12h12M8 18h12M4 6h.01M4 12h.01M4 18h.01" />
  </Icon>
)

/** An attachment on a message (review/Messages.tsx). */
export const IconPaperclip = (p: IconProps) => (
  <Icon {...p}>
    <path d="M20 11.5 12 19.5a5 5 0 0 1-7-7l8-8a3.5 3.5 0 0 1 5 5l-8 8a2 2 0 0 1-3-3l7-7" />
  </Icon>
)

/** Upvotes on a post (review/Post.tsx). */
export const IconUpvote = (p: IconProps) => (
  <Icon {...p}>
    <path d="M12 4 5 12h4.5v8h5v-8H19z" />
  </Icon>
)

/** Comments on a post (review/Post.tsx). */
export const IconComment = (p: IconProps) => (
  <Icon {...p}>
    <path d="M20 12a8 8 0 0 1-11.6 7.1L4 20l1-4.2A8 8 0 1 1 20 12z" />
  </Icon>
)

/** Reposts of a post (review/Post.tsx). */
export const IconRepost = (p: IconProps) => (
  <Icon {...p}>
    <path d="M7 7h11l-3-3M17 17H6l3 3M18 7v5M6 17v-5" />
  </Icon>
)

/** Likes on a post (review/Post.tsx). */
export const IconHeart = (p: IconProps) => (
  <Icon {...p}>
    <path d="M12 20s-7-4.4-7-10a4 4 0 0 1 7-2.6A4 4 0 0 1 19 10c0 5.6-7 10-7 10z" />
  </Icon>
)

/** Views of a post (review/Post.tsx). Bars of unequal height, unlike the analytics glyph. */
export const IconViews = (p: IconProps) => (
  <Icon {...p}>
    <path d="M5 20V10M10 20V4M15 20v-7M20 20v-4" />
  </Icon>
)

/** The star path, on a 16 grid, the way the review pages drew a rating. */
const STAR = 'M8 1.3l2 4.2 4.6.6-3.4 3.2.9 4.6L8 11.6l-4.1 2.3.9-4.6L1.4 6.1 6 5.5z'

/**
 * A rating, as five outlined stars with the filled ones laid over them, cut at
 * the rating so a half star shows (Reviews.tsx and its stylesheet). Not
 * decorative: it carries its own accessible name.
 */
export function Stars({ rating, className }: { rating: number; className?: string }) {
  const n = Math.max(0, Math.min(5, Number.isFinite(rating) ? rating : 0))
  const row = (
    <svg viewBox="0 0 80 16" aria-hidden="true" focusable="false" className="block h-4 w-20 max-w-none">
      {[0, 1, 2, 3, 4].map((i) => (
        <path key={i} d={STAR} transform={`translate(${i * 16} 0)`} fill="currentColor" />
      ))}
    </svg>
  )
  return (
    <span role="img" aria-label={`${n} of 5 stars`} title={`${n} of 5 stars`} className={cn('relative inline-block h-4 w-20 shrink-0 text-line-strong', className)}>
      {row}
      <span className="absolute inset-y-0 left-0 overflow-hidden text-warning" style={{ width: `${n * 20}%` }}>
        {row}
      </span>
    </span>
  )
}
